import React, { useState } from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
  ActivityIndicator,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { goBackOrHome } from '../../navigation/goBackOrHome';
import Toast from 'react-native-toast-message';
import { BrandLogo } from '../../components';
import { fpColors } from '../../styles/theme';
import { useAppDispatch } from '../../store/store';
import { createGroup, type GroupKind } from './groupsSlice';

/**
 * CreateGroupScreen — Sprint 1 transitional version.
 *
 * Old shape collected `permission_level` + per-permission flags. The new
 * v1 API model dropped those fields (handoffs now control trust via
 * `auto_approve_handoff` instead). This screen now collects only:
 *   - Name (required)
 *   - Kind (TEAM / FAMILY / FRIENDS / NEIGHBOURS — BE-200 enum)
 *   - Description (optional)
 *
 * FE-202 — kind picker rewritten from a 3-button chip row (CREW /
 * FAMILY / TEAM) to a 2×2 tile grid (TEAM / FAMILY / FRIENDS /
 * NEIGHBOURS) with icons + descriptions. PERSONAL is intentionally
 * NOT selectable — it's an auto-created hidden bucket every user has.
 */

interface KindOption {
  kind: Extract<GroupKind, 'TEAM' | 'FAMILY' | 'FRIENDS' | 'NEIGHBOURS'>;
  label: string;
  description: string;
  icon: keyof typeof MaterialIcons.glyphMap;
}

const KIND_OPTIONS: KindOption[] = [
  {
    kind: 'TEAM',
    label: 'Team',
    description: 'Work, sub-trades, gear sharing',
    icon: 'groups',
  },
  {
    kind: 'FAMILY',
    label: 'Family',
    description: 'Loved ones in one household',
    icon: 'family-restroom',
  },
  {
    kind: 'FRIENDS',
    label: 'Friends',
    description: 'A tight-knit circle',
    icon: 'people-alt',
  },
  {
    kind: 'NEIGHBOURS',
    label: 'Neighbours',
    description: 'Block or neighbourhood',
    icon: 'location-city',
  },
];

export const CreateGroupScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();

  const [name, setName] = useState('');
  const [kind, setKind] = useState<GroupKind>('TEAM');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const result = await dispatch(
        createGroup({
          name: trimmed,
          kind,
          description: description.trim() || undefined,
        }),
      ).unwrap();
      Toast.show({ type: 'success', text1: `Created ${result.name}` });
      goBackOrHome(navigation);
    } catch (err: any) {
      Toast.show({
        type: 'error',
        text1: 'Could not create',
        text2: typeof err === 'string' ? err : 'Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <View style={styles.page}>
      <View style={styles.topBar}>
        <View style={styles.topBarInner}>
          <Pressable
            onPress={() => goBackOrHome(navigation)}
            hitSlop={8}
            accessibilityLabel="Back"
          >
            <MaterialIcons name="arrow-back" size={22} color={fpColors.onSurface} />
          </Pressable>
          <BrandLogo variant="horizontal" height={20} tone="color" />
          <View style={{ width: 22 }} />
        </View>
      </View>

      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        style={{ flex: 1 }}
      >
        <ScrollView contentContainerStyle={styles.scroll}>
          <Text style={styles.h1}>New circle</Text>
          <Text style={styles.subtitle}>
            Create a circle to share inventory with family, sub-trades, friends,
            or your neighbours.
          </Text>

          <View style={styles.field}>
            <Text style={styles.label}>What kind of circle is this?</Text>
            <View
              style={styles.kindGrid}
              accessibilityRole="radiogroup"
            >
              {KIND_OPTIONS.map((opt) => {
                const selected = kind === opt.kind;
                return (
                  <Pressable
                    key={opt.kind}
                    onPress={() => setKind(opt.kind)}
                    style={({ pressed }) => [
                      styles.kindTile,
                      selected && styles.kindTileActive,
                      pressed && { opacity: 0.88 },
                    ]}
                    accessibilityRole="radio"
                    accessibilityState={{ selected }}
                    accessibilityLabel={`${opt.label}: ${opt.description}`}
                    testID={`create-group-kind-${opt.kind.toLowerCase()}`}
                  >
                    <View
                      style={[
                        styles.kindIconWrap,
                        selected && styles.kindIconWrapActive,
                      ]}
                    >
                      <MaterialIcons
                        name={opt.icon}
                        size={22}
                        color={
                          selected ? fpColors.onPrimary : fpColors.primary
                        }
                      />
                    </View>
                    <Text
                      style={[
                        styles.kindTileLabel,
                        selected && styles.kindTileLabelActive,
                      ]}
                    >
                      {opt.label}
                    </Text>
                    <Text
                      style={[
                        styles.kindTileDesc,
                        selected && styles.kindTileDescActive,
                      ]}
                      numberOfLines={2}
                    >
                      {opt.description}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>Name</Text>
            <TextInput
              value={name}
              onChangeText={setName}
              style={styles.input}
              placeholder="Marcus's Kit"
              placeholderTextColor={fpColors.outline}
              autoFocus
              maxLength={120}
            />
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>Description (optional)</Text>
            <TextInput
              value={description}
              onChangeText={setDescription}
              style={[styles.input, styles.inputMulti]}
              placeholder="Tools for general renos"
              placeholderTextColor={fpColors.outline}
              multiline
              numberOfLines={2}
            />
          </View>

          <Pressable
            onPress={handleSubmit}
            disabled={!canSubmit}
            style={({ pressed }) => [
              styles.submit,
              !canSubmit && { opacity: 0.45 },
              pressed && canSubmit && { opacity: 0.88 },
            ]}
          >
            {submitting ? (
              <ActivityIndicator color={fpColors.onPrimary} />
            ) : (
              <Text style={styles.submitText}>Create</Text>
            )}
          </Pressable>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: fpColors.background },
  topBar: {
    backgroundColor: fpColors.surfaceContainerLowest,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
    paddingTop: Platform.OS === 'ios' ? 48 : 12,
  },
  topBarInner: {
    height: 56,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
  },
  scroll: { padding: 16, paddingBottom: 32, gap: 14 },
  h1: {
    fontFamily: 'Inter_800ExtraBold',
    fontSize: 24,
    color: fpColors.onSurface,
    letterSpacing: -0.4,
  },
  subtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 20,
    color: fpColors.onSurfaceVariant,
  },
  field: { gap: 8, marginTop: 8 },
  label: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onSurface,
  },
  // FE-202 — 2x2 tile grid for the 4 user-visible kinds.
  kindGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  kindTile: {
    // 2 tiles per row with 10px gap. flexBasis ~48% gives a
    // tiny breathing margin so tiles don't crowd the gap.
    flexBasis: '47%',
    flexGrow: 1,
    paddingVertical: 14,
    paddingHorizontal: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    backgroundColor: fpColors.surfaceContainerLowest,
    gap: 6,
  },
  kindTileActive: {
    backgroundColor: fpColors.primaryContainer,
    borderColor: fpColors.primary,
  },
  kindIconWrap: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: fpColors.primaryContainer,
    alignItems: 'center',
    justifyContent: 'center',
  },
  kindIconWrapActive: {
    backgroundColor: fpColors.primary,
  },
  kindTileLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
    marginTop: 4,
  },
  kindTileLabelActive: {
    color: fpColors.onPrimaryContainer,
  },
  kindTileDesc: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    lineHeight: 16,
    color: fpColors.onSurfaceVariant,
  },
  kindTileDescActive: {
    color: fpColors.onPrimaryContainer,
  },
  input: {
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: fpColors.onSurface,
    backgroundColor: fpColors.surfaceContainerLow,
  },
  inputMulti: { minHeight: 64, textAlignVertical: 'top' },
  submit: {
    marginTop: 18,
    paddingVertical: 16,
    borderRadius: 12,
    backgroundColor: fpColors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  submitText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onPrimary,
  },
});
